import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { DECOMPOSABLE_ENTITY_TYPES } from "../../entity-decomposition/entityDecompositions";

const DecomposableEntityTypeSchema = z.enum(
  DECOMPOSABLE_ENTITY_TYPES as [
    (typeof DECOMPOSABLE_ENTITY_TYPES)[number],
    ...(typeof DECOMPOSABLE_ENTITY_TYPES)[number][],
  ],
);

/**
 * Decompose Entity Step
 *
 * Takes any entity reference and exposes its fields as individual output
 * sockets, so a workflow can branch on or reuse one field of an entity it
 * already holds:
 *
 * ```
 * create_ap_invoice_draft ─ apInvoice ─→ decompose_entity ─ supplierId ─→ Switch
 *                                                        └ receivedAt ─→ update_ap_invoice_field.value
 * ```
 *
 * The outputs come from `config.entityType`, which the editor sets from the
 * type of the socket wired into `entity` — plugging in an AP invoice gives the
 * invoice's fields. Which fields exist, and their socket ids, are declared in
 * `ENTITY_DECOMPOSITIONS` (packages/workflows/src/entity-decomposition), a
 * locked public contract: see the AGENTS.md there before changing it.
 *
 * A field that is null on the row leaves its socket unset, which skips any
 * step that requires it.
 */
export const decomposeEntityStep = defineStepConfig({
  type: "decompose_entity",
  name: "Decompose Entity",
  description:
    "Split an entity (an AP invoice, a supplier, …) into one output per field, for branching on or reusing a field",
  category: "Data Access",

  inputSchema: z.object({
    entity: SystemEntitySchema.describe(
      "The entity to decompose. Its type decides the output sockets.",
    ),
  }),

  // Output sockets are generated from config.entityType (see
  // generateDecomposeEntityOutputSockets). A record so the dynamic keys
  // survive Zod parsing, as with switch.
  outputSchema: z.record(z.string(), z.any()),

  configSchema: z.object({
    entityType: DecomposableEntityTypeSchema.optional().describe(
      "The type of entity being decomposed. Set automatically when an entity socket is wired in.",
    ),
  }),

  emits: [] as const,
});
